import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Navigate } from "react-router-dom";
import { toast } from "react-toastify";
import EventBus from "../common/EventBus";
import adminService from "../services/admin.service";

const createEmptyStore = () => ({
    storeName: "",
    openaiVectorStoreId: "",
    lastError: "",
    files: []
});

const getErrorMessage = (error, defaultMsg) => {
    const data = error?.response?.data;
    if (typeof data === 'string') return data;
    if (data?.message) return data.message;
    if (data?.error) return data.error;
    if (data) return JSON.stringify(data);
    return error?.message || defaultMsg;
};

const createEmptyAgent = () => ({
    code: "",
    name: "",
    description: "",
    instructions: "",
    recommendedModel: "",
    thinkingEffort: "medium",
    webSearchEnabled: false,
    imageGenerationEnabled: false,
    codeInterpreterEnabled: false,
    canvasEnabled: false,
    appsEnabled: false,
    requiresPhoto: false,
    isActive: true,
    isDefault: false,
    configJson: "",
    knowledgeStores: []
});

const MODEL_MODE_ORDER = ["Standard", "Pro"];
const THINKING_EFFORT_LABELS = {
    low: "Low",
    medium: "Medium",
    high: "High",
    xhigh: "XHigh",
    max: "Max"
};
const MODE_DESCRIPTIONS = {
    Standard: "Standard performance and accuracy",
    Pro: "Research-grade quality-first intelligence"
};

const formatUsageMb = (usageBytes) => {
    if (usageBytes === null || usageBytes === undefined || usageBytes === "") {
        return "-";
    }

    const bytes = Number(usageBytes);
    if (Number.isNaN(bytes)) {
        return "-";
    }

    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

const getVersionRank = (versionLabel = "") => {
    if (versionLabel.includes("Sol")) return 2;
    if (versionLabel.includes("Terra")) return 1;
    
    const parsed = parseFloat(versionLabel);
    return Number.isNaN(parsed) ? Number.NEGATIVE_INFINITY : parsed;
};

const getModeRank = (modeLabel = "") => {
    const index = MODEL_MODE_ORDER.indexOf(modeLabel);
    return index === -1 ? MODEL_MODE_ORDER.length : index;
};

const isStableModelId = (modelId = "") => !/(preview|snapshot)/i.test(modelId);

const mergeThinkingEfforts = (first = [], second = []) => {
    const order = ["low", "medium", "high", "xhigh", "max"];
    return [...new Set([...(first || []), ...(second || [])])]
        .sort((a, b) => order.indexOf(a) - order.indexOf(b));
};

const pickPreferredModelOption = (currentOption, nextOption, selectedModelId) => {
    if (!currentOption) {
        return nextOption;
    }
    if (currentOption.id === selectedModelId) {
        return {
            ...currentOption,
            supportedThinkingEfforts: mergeThinkingEfforts(currentOption.supportedThinkingEfforts, nextOption.supportedThinkingEfforts)
        };
    }
    if (nextOption.id === selectedModelId) {
        return {
            ...nextOption,
            supportedThinkingEfforts: mergeThinkingEfforts(currentOption.supportedThinkingEfforts, nextOption.supportedThinkingEfforts)
        };
    }

    const currentStable = isStableModelId(currentOption.id);
    const nextStable = isStableModelId(nextOption.id);
    if (currentStable !== nextStable) {
        return {
            ...(nextStable ? nextOption : currentOption),
            supportedThinkingEfforts: mergeThinkingEfforts(currentOption.supportedThinkingEfforts, nextOption.supportedThinkingEfforts)
        };
    }

    if (nextOption.id.length !== currentOption.id.length) {
        return {
            ...(nextOption.id.length < currentOption.id.length ? nextOption : currentOption),
            supportedThinkingEfforts: mergeThinkingEfforts(currentOption.supportedThinkingEfforts, nextOption.supportedThinkingEfforts)
        };
    }

    return {
        ...(nextOption.id.localeCompare(currentOption.id) < 0 ? nextOption : currentOption),
        supportedThinkingEfforts: mergeThinkingEfforts(currentOption.supportedThinkingEfforts, nextOption.supportedThinkingEfforts)
    };
};

const GptConfigManager = () => {
    const { user: currentUser } = useSelector((state) => state.auth);
    const [agents, setAgents] = useState([]);
    const [modelOptions, setModelOptions] = useState([]);
    const [vectorStoreOptions, setVectorStoreOptions] = useState([]);
    const [selectedId, setSelectedId] = useState(null);
    const [form, setForm] = useState(createEmptyAgent());
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [loadError, setLoadError] = useState("");

    const canAccess = currentUser?.roles?.includes("ROLE_AI_ADMIN");

    const groupedModelOptions = useMemo(() => {
        const grouped = {};

        modelOptions.forEach((option) => {
            const versionLabel = option.versionLabel || option.label || option.id;
            const modeLabel = option.modeLabel || "Thinking";

            if (!grouped[versionLabel]) {
                grouped[versionLabel] = {};
            }
            const normalizedOption = {
                ...option,
                versionLabel,
                modeLabel
            };
            grouped[versionLabel][modeLabel] = pickPreferredModelOption(
                grouped[versionLabel][modeLabel],
                normalizedOption,
                form.recommendedModel
            );
        });

        return Object.entries(grouped)
            .sort((a, b) => getVersionRank(b[0]) - getVersionRank(a[0]) || a[0].localeCompare(b[0]))
            .map(([versionLabel, optionsByMode]) => ({
                versionLabel,
                options: Object.values(optionsByMode).sort((a, b) =>
                    getModeRank(a.modeLabel) - getModeRank(b.modeLabel) || a.id.localeCompare(b.id)
                )
            }));
    }, [form.recommendedModel, modelOptions]);

    const flattenedModelOptions = useMemo(
        () => groupedModelOptions.flatMap((group) => group.options),
        [groupedModelOptions]
    );

    const selectedModelOption = useMemo(() => {
        const exactMatch = flattenedModelOptions.find((option) => option.id === form.recommendedModel);
        if (exactMatch) {
            return exactMatch;
        }

        const rawSelected = modelOptions.find((option) => option.id === form.recommendedModel);
        if (!rawSelected) {
            return null;
        }

        const versionLabel = rawSelected.versionLabel || rawSelected.label || rawSelected.id;
        const modeLabel = rawSelected.modeLabel || "Thinking";
        return flattenedModelOptions.find((option) =>
            option.versionLabel === versionLabel && option.modeLabel === modeLabel
        ) || null;
    }, [flattenedModelOptions, form.recommendedModel, modelOptions]);

    const selectedVersionLabel = selectedModelOption?.versionLabel
        || groupedModelOptions[0]?.versionLabel
        || "";

    const availableModesForVersion = useMemo(() => {
        const versionGroup = groupedModelOptions.find((group) => group.versionLabel === selectedVersionLabel);
        return versionGroup?.options || [];
    }, [groupedModelOptions, selectedVersionLabel]);

    const selectedThinkingEfforts = selectedModelOption?.supportedThinkingEfforts || [];

    const usesCustomModelOverride = Boolean(
        form.recommendedModel && !flattenedModelOptions.some((option) => option.id === form.recommendedModel)
    );

    const loadAgents = useCallback(() => {
        setLoading(true);
        setLoadError("");
        adminService.getAiAgents().then(
            (response) => {
                const data = response.data || [];
                setAgents(data);
                setLoading(false);
                if (selectedId) {
                    const stillExists = data.some((agent) => agent.id === selectedId);
                    if (!stillExists) {
                        setSelectedId(null);
                        setForm(createEmptyAgent());
                    }
                }
            },
            (error) => {
                setLoading(false);
                const message = getErrorMessage(error, "Failed to load GPT configurations.");
                setLoadError(message);
                EventBus.dispatch("SHOW_ERROR_MODAL", { title: "Error", message: message });
            }
        );
    }, [selectedId]);

    useEffect(() => {
        if (!canAccess) {
            return;
        }
        loadAgents();
        adminService.getAiAgentModels().then(
            (response) => setModelOptions(response.data || []),
            () => toast.error("Failed to load recommended models.")
        );
        adminService.getAiAgentVectorStores().then(
            (response) => setVectorStoreOptions(response.data || []),
            () => toast.error("Failed to load vector stores.")
        );
    }, [canAccess, loadAgents]);

    const selectAgent = (id) => {
        setLoading(true);
        adminService.getAiAgent(id).then(
            (response) => {
                setSelectedId(id);
                setForm(response.data || createEmptyAgent());
                setLoading(false);
            },
            () => {
                setLoading(false);
                EventBus.dispatch("SHOW_ERROR_MODAL", { title: "Error", message: "Failed to load GPT configuration details." });
            }
        );
    };

    const resetForNew = () => {
        setSelectedId(null);
        setForm(createEmptyAgent());
    };

    const cloneSelectedAgent = () => {
        setSelectedId(null);
        setForm((prev) => ({
            ...prev,
            code: prev.code ? `${prev.code}_copy` : "",
            name: prev.name ? `${prev.name} (Copy)` : "",
            isDefault: false,
            knowledgeStores: (prev.knowledgeStores || []).map((store) => ({
                storeName: store.storeName || "",
                openaiVectorStoreId: store.openaiVectorStoreId || "",
                lastError: store.lastError || "",
                files: store.files || []
            }))
        }));
        toast.info("Cloned selected configuration. Edit code/name and click 'Create GPT Config' to save.");
    };

    const updateField = (field, value) => {
        setForm((prev) => ({ ...prev, [field]: value }));
    };

    const updateStore = (index, field, value) => {
        setForm((prev) => {
            const knowledgeStores = [...(prev.knowledgeStores || [])];
            knowledgeStores[index] = { ...knowledgeStores[index], [field]: value };
            return { ...prev, knowledgeStores };
        });
    };

    const selectVectorStore = (index, vectorStoreId) => {
        const selectedStore = vectorStoreOptions.find((option) => option.id === vectorStoreId);
        setForm((prev) => {
            const knowledgeStores = [...(prev.knowledgeStores || [])];
            knowledgeStores[index] = {
                ...knowledgeStores[index],
                openaiVectorStoreId: vectorStoreId,
                storeName: selectedStore?.name || knowledgeStores[index]?.storeName || ""
            };
            return { ...prev, knowledgeStores };
        });
    };

    const getAvailableVectorStoreOptions = (storeIndex) => {
        const selectedByOthers = new Set(
            (form.knowledgeStores || [])
                .map((store, index) => index === storeIndex ? null : (store.openaiVectorStoreId || "").trim())
                .filter((id) => id && id.length > 0)
        );

        return vectorStoreOptions.filter((option) =>
            option.id === form.knowledgeStores?.[storeIndex]?.openaiVectorStoreId || !selectedByOthers.has(option.id)
        );
    };

    const updateRecommendedVersion = (versionLabel) => {
        const versionGroup = groupedModelOptions.find((group) => group.versionLabel === versionLabel);
        if (!versionGroup?.options?.length) {
            return;
        }

        const preferredMode = selectedModelOption?.modeLabel;
        const nextOption = versionGroup.options.find((option) => option.modeLabel === preferredMode) || versionGroup.options[0];
        updateField("recommendedModel", nextOption.id);

        const supportedEfforts = nextOption.supportedThinkingEfforts || [];
        if (supportedEfforts.length > 0 && !supportedEfforts.includes(form.thinkingEffort)) {
            updateField("thinkingEffort", supportedEfforts[0]);
        }
    };

    const updateRecommendedMode = (modelId) => {
        const nextOption = flattenedModelOptions.find((option) => option.id === modelId);
        updateField("recommendedModel", modelId);

        const supportedEfforts = nextOption?.supportedThinkingEfforts || [];
        if (supportedEfforts.length === 0) {
            updateField("thinkingEffort", "medium");
            return;
        }

        if (!supportedEfforts.includes(form.thinkingEffort)) {
            updateField("thinkingEffort", supportedEfforts[0]);
        }
    };

    const addStore = () => {
        if ((form.knowledgeStores || []).length >= 2) {
            toast.warning("A GPT configuration can have at most 2 vector stores.");
            return;
        }

        setForm((prev) => ({
            ...prev,
            knowledgeStores: [...(prev.knowledgeStores || []), createEmptyStore()]
        }));
    };

    const removeStore = (index) => {
        setForm((prev) => ({
            ...prev,
            knowledgeStores: (prev.knowledgeStores || []).filter((_, i) => i !== index)
        }));
    };

    const buildPayload = () => ({
        ...form,
        code: (form.code || "").trim(),
        name: (form.name || "").trim(),
        description: (form.description || "").trim(),
        instructions: form.instructions || "",
        recommendedModel: (form.recommendedModel || "").trim(),
        thinkingEffort: (form.thinkingEffort || "").trim() || "medium",
        isActive: !!form.isActive,
        isDefault: !!form.isDefault,
        configJson: form.configJson || "",
        conversationStarters: [],
        knowledgeStores: (form.knowledgeStores || [])
            .map((store) => ({
                ...store,
                storeName: (store.storeName || "").trim(),
                openaiVectorStoreId: (store.openaiVectorStoreId || "").trim(),
                lastError: store.lastError || ""
            }))
            .filter((store) => store.storeName.length > 0)
    });

    const hasDuplicateVectorStores = () => {
        const vectorStoreIds = (form.knowledgeStores || [])
            .map((store) => (store.openaiVectorStoreId || "").trim())
            .filter((id) => id.length > 0);

        return new Set(vectorStoreIds).size !== vectorStoreIds.length;
    };

    const saveAgent = () => {
        if (!(form.code || "").trim() || !(form.name || "").trim()) {
            EventBus.dispatch("SHOW_ERROR_MODAL", { title: "Error", message: "Code and Name are required." });
            return;
        }

        if ((form.knowledgeStores || []).length > 2) {
            EventBus.dispatch("SHOW_ERROR_MODAL", { title: "Error", message: "A GPT configuration can have at most 2 vector stores." });
            return;
        }
        if (hasDuplicateVectorStores()) {
            EventBus.dispatch("SHOW_ERROR_MODAL", { title: "Error", message: "The same OpenAI vector store cannot be selected more than once." });
            return;
        }

        const payload = buildPayload();
        const request = selectedId
            ? adminService.updateAiAgent(selectedId, payload)
            : adminService.createAiAgent(payload);

        setSaving(true);
        request.then(
            (response) => {
                const saved = response.data;
                toast.success(selectedId ? "GPT configuration updated." : "GPT configuration created.");
                setSelectedId(saved.id);
                setForm(saved);
                setSaving(false);
                loadAgents();
            },
            (error) => {
                setSaving(false);
                EventBus.dispatch("SHOW_ERROR_MODAL", { title: "Error", message: getErrorMessage(error, "Failed to save GPT configuration.") });
            }
        );
    };

    if (!currentUser) {
        return <Navigate to="/login" />;
    }

    if (!canAccess) {
        return (
            <div className="container">
                <header className="jumbotron">
                    <h3><strong>Unauthorized:</strong> You must have `ROLE_AI_ADMIN` to access GPT Configure.</h3>
                </header>
            </div>
        );
    }

    return (
        <div className="container-fluid py-4" style={{ background: 'var(--bg-gradient)', minHeight: 'calc(100vh - 76px)' }}>
            <div className="d-flex justify-content-between align-items-center mb-3">
                <div>
                    <h2 className="mb-1">GPT Configure</h2>
                    <p className="text-muted mb-0">Manage prompt definitions and vector-store metadata.</p>
                </div>
                <div className="d-flex gap-2">
                    <button className="btn btn-outline-secondary rounded-pill px-4 shadow-sm me-2" onClick={loadAgents} disabled={loading || saving}>
                        {loading && <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>}
                        {loading ? "Refreshing..." : "Refresh"}
                    </button>
                    {selectedId && (
                        <button className="btn btn-outline-primary rounded-pill px-4 shadow-sm me-2" onClick={cloneSelectedAgent} disabled={saving || loading}>
                            Copy Config
                        </button>
                    )}
                    <button className="btn btn-primary rounded-pill px-4 shadow-sm" onClick={resetForNew} disabled={saving}>
                        New Config
                    </button>
                </div>
            </div>

            <div className="row">
                <div className="col-lg-4 mb-4">
                    <div className="card border-0 shadow-lg rounded-4 overflow-hidden mb-4">
                        <div className="card-header bg-white border-0 py-3 px-4 fw-bold text-dark fs-5">Existing Configurations</div>
                        <div className="list-group list-group-flush">
                            {loading && agents.length === 0 && (
                                <div className="list-group-item text-muted">Loading...</div>
                            )}
                            {!loading && !!loadError && (
                                <div className="list-group-item text-danger">
                                    Failed to load GPT configurations.
                                    <div className="small mt-1">{String(loadError)}</div>
                                </div>
                            )}
                            {!loading && !loadError && agents.length === 0 && (
                                <div className="list-group-item text-muted">No GPT configurations yet.</div>
                            )}
                            {agents.map((agent) => (
                                <button
                                    type="button"
                                    key={agent.id}
                                    className={`list-group-item list-group-item-action ${selectedId === agent.id ? "active" : ""}`}
                                    onClick={() => selectAgent(agent.id)}
                                >
                                    <div className="fw-bold">{agent.name}</div>
                                    <small>{agent.code}</small>
                                    {agent.requiresPhoto && (
                                        <span className="badge bg-warning text-dark ms-2">Photo Required</span>
                                    )}
                                    {agent.isDefault && (
                                        <span className="badge bg-primary ms-2">Default</span>
                                    )}
                                    {!agent.isActive && (
                                        <span className="badge bg-secondary ms-2">Inactive</span>
                                    )}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="col-lg-8">
                    <div className="card border-0 shadow-lg rounded-4 overflow-hidden mb-4">
                        <div className="card-header bg-white border-0 py-3 px-4 fw-bold text-dark fs-5">
                            {selectedId ? "Edit GPT Configuration" : "Create GPT Configuration"}
                        </div>
                        <div className="card-body">
                            <div className="row mb-3">
                                <div className="col-md-6">
                                    <label className="form-label">Code</label>
                                    <input className="form-control" value={form.code || ""} onChange={(e) => updateField("code", e.target.value)} />
                                </div>
                                <div className="col-md-6">
                                    <label className="form-label">Name</label>
                                    <input className="form-control" value={form.name || ""} onChange={(e) => updateField("name", e.target.value)} />
                                </div>
                            </div>

                            <div className="row mb-3">
                                <div className="col-md-12">
                                    <label className="form-label">Description</label>
                                    <input className="form-control" value={form.description || ""} onChange={(e) => updateField("description", e.target.value)} />
                                </div>
                            </div>

                            <div className="card border-0 shadow-sm mb-4" style={{ backgroundColor: "#f8f5ef", borderRadius: "20px" }}>
                                <div className="card-body p-4">
                                    <h5 className="mb-4">Intelligence</h5>

                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <div>
                                            <div className="fw-semibold">Model</div>
                                            <div className="text-muted small">Choose a model family first.</div>
                                        </div>
                                        <div style={{ minWidth: "160px" }}>
                                            <select
                                                className="form-select border-0 shadow-sm"
                                                value={selectedVersionLabel}
                                                onChange={(e) => updateRecommendedVersion(e.target.value)}
                                                disabled={groupedModelOptions.length === 0}
                                            >
                                                {groupedModelOptions.length === 0 && (
                                                    <option value="">No models available</option>
                                                )}
                                                {groupedModelOptions.map((group) => (
                                                    <option key={group.versionLabel} value={group.versionLabel}>
                                                        {group.versionLabel}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>

                                    <hr />

                                    <div className="mb-4">
                                        {availableModesForVersion.length === 0 && (
                                            <div className="text-muted small">No recommended model modes were returned from OpenAI.</div>
                                        )}
                                        {availableModesForVersion.map((option) => {
                                            const isSelected = form.recommendedModel === option.id;
                                            return (
                                                <button
                                                    key={option.id}
                                                    type="button"
                                                    className={`btn w-100 text-start border rounded-4 mb-3 ${isSelected ? "border-dark bg-white" : "bg-transparent"}`}
                                                    style={{ padding: "16px 18px" }}
                                                    onClick={() => updateRecommendedMode(option.id)}
                                                >
                                                    <div className="d-flex justify-content-between align-items-start">
                                                        <div>
                                                            <div className="fw-semibold fs-5">{option.modeLabel}</div>
                                                            <div className="text-muted">{option.description || MODE_DESCRIPTIONS[option.modeLabel] || ""}</div>
                                                        </div>
                                                        <div className="fw-semibold">{isSelected ? "Selected" : ""}</div>
                                                    </div>
                                                </button>
                                            );
                                        })}
                                    </div>

                                    <hr />

                                    <div className="d-flex justify-content-between align-items-start">
                                        <div className="me-3">
                                            <div className="fw-semibold">Thinking effort</div>
                                            <div className="text-muted small">
                                                More effort can give more thorough answers, but replies can take longer.
                                            </div>
                                        </div>
                                        <div style={{ minWidth: "180px" }}>
                                            <select
                                                className="form-select border-0 shadow-sm"
                                                value={selectedThinkingEfforts.includes(form.thinkingEffort) ? form.thinkingEffort : (selectedThinkingEfforts[0] || form.thinkingEffort || "medium")}
                                                onChange={(e) => updateField("thinkingEffort", e.target.value)}
                                                disabled={selectedThinkingEfforts.length === 0}
                                            >
                                                {(selectedThinkingEfforts.length ? selectedThinkingEfforts : ["medium"]).map((effort) => (
                                                    <option key={effort} value={effort}>
                                                        {THINKING_EFFORT_LABELS[effort] || effort}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>

                                    <div className="mt-3 text-muted small">
                                        {selectedModelOption ? (
                                            <>
                                                Selected model ID: <code>{selectedModelOption.id}</code>
                                            </>
                                        ) : (
                                            "Select a recommended model to preview the actual model ID."
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="mb-3">
                                <label className="form-label">Custom Recommended Model Override</label>
                                <input
                                    className="form-control"
                                    value={form.recommendedModel || ""}
                                    onChange={(e) => updateField("recommendedModel", e.target.value)}
                                    placeholder="Optional custom model ID"
                                />
                                <small className="text-muted">
                                    {usesCustomModelOverride
                                        ? "This value is not in the recommended OpenAI list, so it is being treated as a custom override."
                                        : "Leave the selected recommended model as-is unless you need a specific OpenAI model ID."}
                                </small>
                            </div>

                            <div className="mb-3">
                                <label className="form-label">Instructions</label>
                                <textarea className="form-control" rows="7" value={form.instructions || ""} onChange={(e) => updateField("instructions", e.target.value)} />
                            </div>

                            <div className="mb-4">
                                <label className="form-label">Extra Config JSON</label>
                                <textarea className="form-control font-monospace" rows="5" value={form.configJson || ""} onChange={(e) => updateField("configJson", e.target.value)} />
                            </div>

                            <h5 className="text-secondary border-bottom pb-2 mb-3">Capabilities</h5>
                            <div className="row mb-4">
                                {[
                                    ["webSearchEnabled", "Web Search"],
                                    ["imageGenerationEnabled", "Image Generation"],
                                    ["codeInterpreterEnabled", "Code Interpreter"],
                                    ["canvasEnabled", "Canvas"],
                                    ["appsEnabled", "Apps"]
                                ].map(([field, label]) => (
                                    <div className="col-md-4 mb-2" key={field}>
                                        <div className="form-check form-switch">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                checked={!!form[field]}
                                                id={field}
                                                onChange={(e) => updateField(field, e.target.checked)}
                                            />
                                            <label className="form-check-label" htmlFor={field}>{label}</label>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <h5 className="text-secondary border-bottom pb-2 mb-3">Status</h5>
                            <div className="row mb-4">
                                <div className="col-md-4 mb-2">
                                    <div className="form-check form-switch">
                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            checked={!!form.isActive}
                                            id="isActive"
                                            onChange={(e) => {
                                                const checked = e.target.checked;
                                                if (!checked) {
                                                    setForm((prev) => ({ ...prev, isActive: false, isDefault: false }));
                                                } else {
                                                    updateField("isActive", true);
                                                }
                                            }}
                                        />
                                        <label className="form-check-label" htmlFor="isActive">Active</label>
                                    </div>
                                </div>
                                <div className="col-md-4 mb-2">
                                    <div className="form-check form-switch">
                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            checked={!!form.isDefault}
                                            id="isDefault"
                                            onChange={(e) => {
                                                const checked = e.target.checked;
                                                if (checked) {
                                                    setForm((prev) => ({ ...prev, isActive: true, isDefault: true }));
                                                } else {
                                                    updateField("isDefault", false);
                                                }
                                            }}
                                        />
                                        <label className="form-check-label" htmlFor="isDefault">Default Config</label>
                                    </div>
                                </div>
                            </div>

                            <h5 className="text-secondary border-bottom pb-2 mb-3">Input Requirements</h5>
                            <div className="row mb-4">
                                <div className="col-md-4 mb-2">
                                    <div className="form-check form-switch">
                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            checked={!!form.requiresPhoto}
                                            id="requiresPhoto"
                                            onChange={(e) => updateField("requiresPhoto", e.target.checked)}
                                        />
                                        <label className="form-check-label" htmlFor="requiresPhoto">Photo Required</label>
                                    </div>
                                </div>
                            </div>

                            <div className="d-flex justify-content-between align-items-center border-bottom pb-2 mb-3">
                                <h5 className="text-secondary mb-0">Knowledge Stores</h5>
                                <button
                                    className="btn btn-outline-primary btn-sm"
                                    onClick={addStore}
                                    type="button"
                                    disabled={(form.knowledgeStores || []).length >= 2}
                                >
                                    Add Store
                                </button>
                            </div>

                            <div className="text-muted small mb-3">
                                Maximum 2 vector stores per GPT configuration.
                            </div>

                            {(form.knowledgeStores || []).length === 0 && (
                                <div className="alert alert-light border">No knowledge stores configured yet.</div>
                            )}

                            {(form.knowledgeStores || []).map((store, storeIndex) => (
                                <div className="card mb-3 border" key={`store-${storeIndex}`}>
                                    <div className="card-header d-flex justify-content-between align-items-center">
                                        <strong>Store {storeIndex + 1}</strong>
                                        <button className="btn btn-outline-danger btn-sm" type="button" onClick={() => removeStore(storeIndex)}>
                                            Remove Store
                                        </button>
                                    </div>
                                    <div className="card-body">
                                        <div className="row mb-3">
                                            <div className="col-md-6">
                                                <label className="form-label">Store Name</label>
                                                <input className="form-control" value={store.storeName || ""} onChange={(e) => updateStore(storeIndex, "storeName", e.target.value)} />
                                            </div>
                                            <div className="col-md-6">
                                                <label className="form-label">Pick OpenAI Vector Store</label>
                                                <select
                                                    className="form-select mb-2"
                                                    value={store.openaiVectorStoreId || ""}
                                                    onChange={(e) => selectVectorStore(storeIndex, e.target.value)}
                                                >
                                                    <option value="">Select a vector store...</option>
                                                    {getAvailableVectorStoreOptions(storeIndex).map((option) => (
                                                        <option key={option.id} value={option.id}>
                                                            {(option.name || option.id)} ({option.status || "unknown"})
                                                        </option>
                                                    ))}
                                                </select>
                                                <label className="form-label">OpenAI Vector Store ID</label>
                                                <input className="form-control" value={store.openaiVectorStoreId || ""} onChange={(e) => updateStore(storeIndex, "openaiVectorStoreId", e.target.value)} />
                                                <small className="text-muted">
                                                    Pick by name above, or paste a vector store ID manually.
                                                </small>
                                            </div>
                                        </div>

                                        <div className="row mb-3">
                                            <div className="col-md-4">
                                                <label className="form-label">Live Status</label>
                                                <input className="form-control bg-light" value={store.status || ""} readOnly />
                                            </div>
                                            <div className="col-md-8">
                                                <label className="form-label">Storage Usage</label>
                                                <input className="form-control bg-light" value={formatUsageMb(store.usageBytes)} readOnly />
                                            </div>
                                        </div>

                                        <div className="mt-3">
                                            <strong className="d-block mb-2">Live OpenAI Files</strong>
                                            {!(store.files || []).length && (
                                                <div className="text-muted small">
                                                    {store.openaiVectorStoreId
                                                        ? "No files returned from this vector store."
                                                        : "Save a vector store ID to fetch its current files from OpenAI."}
                                                </div>
                                            )}

                                            {(store.files || []).length > 0 && (
                                                <div className="table-responsive">
                                                    <table className="table table-sm table-bordered align-middle mb-0">
                                                        <thead>
                                                        <tr>
                                                            <th>File Name</th>
                                                            <th>OpenAI File ID</th>
                                                            <th>Status</th>
                                                            <th>Storage Usage</th>
                                                        </tr>
                                                        </thead>
                                                        <tbody>
                                                        {store.files.map((file, fileIndex) => (
                                                            <tr key={`file-${storeIndex}-${fileIndex}`}>
                                                                <td>{file.fileName || "-"}</td>
                                                                <td><code>{file.id}</code></td>
                                                                <td>{file.status || "-"}</td>
                                                                <td>{formatUsageMb(file.usageBytes)}</td>
                                                            </tr>
                                                        ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}

                            <div className="d-flex justify-content-between align-items-center mt-4">
                                <div>
                                    {selectedId && (
                                        <button
                                            type="button"
                                            className="btn btn-outline-secondary rounded-pill px-4 shadow-sm"
                                            onClick={cloneSelectedAgent}
                                            disabled={saving || loading}
                                        >
                                            Copy Config
                                        </button>
                                    )}
                                </div>
                                <div>
                                    <button className="btn btn-premium rounded-pill px-4 shadow-sm" onClick={saveAgent} disabled={saving || loading}>
                                        {saving && <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>}
                                        {saving ? "Saving..." : selectedId ? "Update GPT Config" : "Create GPT Config"}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default GptConfigManager;
